import { LocalizationManager } from '@lib/localization';
import { type Dispatch } from '@reduxjs/toolkit';
import { showSnackbar } from './reducers/snackbarSlice';

export interface ServerError {
  error: {
    status: number;
    data: {
      message: string;
      errors: unknown;
    };
  };
}

/** One field a request was refused for. `code` names the failure, `message` says it in English. */
export interface ApiFieldError {
  path: string;
  code?: string;
  message: string;
}

type ValidationErrorItem = ApiFieldError;

interface ApiResultOptions {
  handle401Error?: boolean;
  /**
   * A refusal that names its fields is left to the caller, which shows each on its field; any
   * other error is still said here.
   */
  quietFieldErrors?: boolean;
  /** Failures the caller says in its own place, by status, so no snackbar announces them. */
  quietStatuses?: number[];
}

/** The fields a failed request was refused for, from a mutation's `error` or a rejected query. */
export const fieldErrorsOf = (error: unknown): ApiFieldError[] => {
  const data = isObjectWithKey(error, 'data') ? error.data
    : isObjectWithKey(error, 'error') && isObjectWithKey(error.error, 'data') ? error.error.data
      : undefined;

  return isObjectWithKey(data, 'errors') && isDataHasMultiError(data.errors) ? data.errors : [];
};

export const handleApiResult = (
  successMessage?: string,
  { handle401Error = false, quietFieldErrors = false, quietStatuses = [] }: ApiResultOptions = {}
) =>
  async (_: unknown,
    { dispatch, queryFulfilled }: { dispatch: Dispatch; queryFulfilled: Promise<{ data: unknown }> }) => {
    try {
      await queryFulfilled;
      if (successMessage != null) {
        dispatch(showSnackbar({ severity: 'success', message: LocalizationManager.t(successMessage) }));
      }
    } catch (error) {
      if (isServerError(error) && quietStatuses.includes(error.error.status)) return;
      if (quietFieldErrors && fieldErrorsOf(error).length > 0) return;

      if (isServerError(error) && (handle401Error || error.error.status != 401)) {
        dispatch(showSnackbar({ severity: 'error', message: apiErrorMessage(error.error.data) }));
      } else {
        console.error('api.error:', error);
      }
    }
  };

/** A server error as text: its message, then each validation error's path and message on a line. */
export const apiErrorMessage = (data: ServerError['error']['data']): string => {
  if (!isDataHasMultiError(data.errors)) return data.message;

  return [data.message, ...data.errors.map((error) => `${error.path}: ${error.message}`)].join('\n');
};

const isObjectWithKey = <T>(obj: unknown, key: string): obj is Record<string, T> => {
  return typeof obj === 'object' && obj !== null && key in obj;
};

export const isServerError = (error: unknown): error is ServerError => {
  return (
    isObjectWithKey(error, 'error') &&
    isObjectWithKey(error.error, 'data') &&
    isObjectWithKey(error.error.data, 'message') &&
    typeof error.error.status === 'number' &&
    typeof error.error.data.message === 'string'
  );
};

const isValidationErrorItem = (error: unknown): error is ValidationErrorItem => {
  return (
    isObjectWithKey<string>(error, 'path') &&
    isObjectWithKey<string>(error, 'message')
  );
};

const isDataHasMultiError = (errors: unknown): errors is ValidationErrorItem[] => {
  return (
    Array.isArray(errors) &&
    errors.length > 0 &&
    errors.every(isValidationErrorItem)
  );
};
